/**
 * Zod schema for the `DocumentBlockerErrorBody` returned by gated
 * mutation routes. Lives here (api/src) so the wire contract is
 * single-sourced; `web/src/hooks/useDocumentBlockerErrorHandler.tsx`
 * imports the schema directly to validate incoming RTKQ errors.
 */
import { z } from "zod";
import { DOCUMENT_ACTIONS, DOCUMENT_BLOCKER_CODES } from "./codes.js";

// `z.enum` preserves the literal-string union when handed a readonly
// tuple of string literals (the `as const` arrays from `codes.ts`).
// This is what binds the runtime parser output to the same union the
// rest of the codebase uses for `DocumentBlockerCode` / `DocumentAction`.
const blockerCodeSchema = z.enum(DOCUMENT_BLOCKER_CODES);
const actionSchema = z.enum(DOCUMENT_ACTIONS);

const fdUserStatusSchema = z.object({
  hasAllRequiredIncomeYears: z.boolean(),
  hasAllRequiredFields: z.boolean(),
  hasAllPartnerApprovals: z.boolean(),
  hasAllSelfApprovals: z.boolean(),
  hasAllApprovals: z.boolean(),
});

const fdStatusSchema = z
  .object({
    isComplete: z.boolean(),
    users: z.record(z.string(), fdUserStatusSchema),
  })
  .nullable();

const blockerSchema = z.object({
  code: blockerCodeSchema,
  meta: z
    .object({
      side: z.enum(["self", "partner"]).optional(),
      sevenDayAnchorAt: z.union([z.string(), z.date()]).optional(),
      unfilledExamples: z.array(z.string()).optional(),
    })
    .optional(),
});

export const documentBlockerErrorBodySchema = z.object({
  error: z.string(),
  code: z.literal("DOCUMENT_BLOCKED"),
  blocker: blockerSchema,
  fdStatus: fdStatusSchema,
  action: actionSchema,
});

export type DocumentBlockerErrorBodyParsed = z.infer<
  typeof documentBlockerErrorBodySchema
>;

// Compile-time check that the runtime Zod parser's output matches the
// source `DocumentBlockerErrorBody` interface. If someone widens the
// interface (e.g. adds a new `meta` field on a blocker) without
// updating this schema, the `extends` constraint here fails and points
// at this file. The export is part of the module's public surface so
// lint doesn't flag it as unused; consumers never need to reference it.
import type { DocumentBlockerErrorBody } from "./assert.js";
type AssertSchemaExtendsSource<T extends DocumentBlockerErrorBody> = T;
export type SchemaSourceMatchAssertion =
  AssertSchemaExtendsSource<DocumentBlockerErrorBodyParsed>;

const fetchBaseQueryErrorShape = z.object({
  status: z.literal(409),
  data: documentBlockerErrorBodySchema,
});

const queryFulfilledRejectionShape = z.object({
  error: fetchBaseQueryErrorShape,
});

/**
 * Parses either an RTKQ `FetchBaseQueryError` from `.unwrap()` or the
 * wrapped rejection produced by `queryFulfilled`. Returns `null` when
 * neither shape matches so callers can fall through to existing UX.
 */
export function tryParseDocumentBlockerError(
  err: unknown,
): DocumentBlockerErrorBodyParsed | null {
  const directResult = fetchBaseQueryErrorShape.safeParse(err);
  if (directResult.success) return directResult.data.data;

  const wrappedResult = queryFulfilledRejectionShape.safeParse(err);
  return wrappedResult.success ? wrappedResult.data.error.data : null;
}
